import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  ChevronsUpDown,
  Info,
  LogOut,
  Search,
} from 'lucide-react';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { buttonVariants } from '@/components/ui/button-variants';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { AnalyticsEvents, trackEvent } from '@/lib/analytics';
import { DTT_JURISDICTIONS } from '@/types/jurisdictions';
import type { DttJurisdiction } from '@/types/jurisdictions';

/** The home entity. Every indexed Double Tax Treaty is Singapore ↔ a partner. */
const HOME_JURISDICTION: DttJurisdiction = 'Singapore';

/** "No treaty" sentinel label shown as the first combobox option. */
const NO_TREATY_LABEL = 'No treaty — general Singapore tax';

interface ComboboxOption {
  /** null = the "No treaty" (general query) option. */
  value: DttJurisdiction | null;
  label: string;
  disabled: boolean;
}

interface EntityDropdownProps {
  /** Listbox heading + analytics direction ("Source" | "Destination"). */
  label: string;
  direction: 'from' | 'to';
  /** Accessible name for the trigger (e.g. "Source entity"). */
  ariaLabel: string;
  placeholder: string;
  value: DttJurisdiction | null;
  /** The entity picked on the other side — disabled here to keep the pair distinct. */
  disabledOption: DttJurisdiction | null;
  /** When set, offer a "No treaty" option so this side can be cleared (general query). */
  allowNone?: boolean;
  /** Explanatory tooltip shown beside the trigger (flags the optional DTT side). */
  tooltip?: string;
  onChange: (jurisdiction: DttJurisdiction | null) => void;
}

/** A searchable single-select combobox over the DTT jurisdictions. */
function EntityDropdown({
  label,
  direction,
  ariaLabel,
  placeholder,
  value,
  disabledOption,
  allowNone = false,
  tooltip,
  onChange,
}: EntityDropdownProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const options = useMemo<ComboboxOption[]>(() => {
    const jurisdictions = DTT_JURISDICTIONS.map((j) => ({
      value: j,
      label: j,
      disabled: j === disabledOption,
    }));
    return allowNone
      ? [
          { value: null, label: NO_TREATY_LABEL, disabled: false },
          ...jurisdictions,
        ]
      : jurisdictions;
  }, [allowNone, disabledOption]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  // Keep the keyboard highlight on the first selectable result as the query
  // narrows the list.
  useEffect(() => {
    const firstSelectable = filtered.findIndex((o) => !o.disabled);
    setActiveIndex(firstSelectable === -1 ? 0 : firstSelectable);
  }, [filtered]);

  function commitSelection(option: ComboboxOption) {
    if (option.disabled) return;
    onChange(option.value);
    trackEvent(AnalyticsEvents.JurisdictionSelected, {
      direction,
      jurisdiction: option.value ?? 'none',
    });
    setOpen(false);
    setQuery('');
  }

  function moveActive(delta: number) {
    if (filtered.length === 0) return;
    let next = activeIndex;
    for (let step = 0; step < filtered.length; step += 1) {
      next = (next + delta + filtered.length) % filtered.length;
      if (!filtered[next].disabled) {
        setActiveIndex(next);
        return;
      }
    }
  }

  function handleSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveActive(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveActive(-1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = filtered[activeIndex];
      if (option) commitSelection(option);
    }
  }

  return (
    <div className="flex items-center gap-1">
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setQuery('');
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={ariaLabel}
            aria-haspopup="listbox"
            aria-expanded={open}
            className={cn(
              buttonVariants({ variant: 'outline', size: 'sm' }),
              'min-w-[10rem] justify-between',
            )}
          >
            <span className={cn(!value && 'text-muted-foreground')}>
              {value ?? placeholder}
            </span>
            <ChevronsUpDown className="ml-1 size-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0">
          <div className="flex items-center gap-2 border-b px-3">
            <Search className="size-4 shrink-0 opacity-50" aria-hidden />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search jurisdictions…"
              aria-label={`Search ${label.toLowerCase()} jurisdictions`}
              className="placeholder:text-muted-foreground h-9 w-full bg-transparent py-1 text-sm outline-none"
            />
          </div>
          <ul
            role="listbox"
            aria-label={label}
            className="max-h-72 overflow-y-auto p-1"
          >
            {filtered.length === 0 ? (
              <li className="text-muted-foreground px-2 py-1.5 text-sm">
                No matches.
              </li>
            ) : (
              filtered.map((option, index) => {
                const selected = option.value === value;
                const active = index === activeIndex && !option.disabled;
                return (
                  <li
                    key={option.label}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={option.disabled || undefined}
                    onClick={() => commitSelection(option)}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={cn(
                      'flex cursor-pointer items-center justify-between rounded-sm px-2 py-1.5 text-sm',
                      active && 'bg-accent text-accent-foreground',
                      option.disabled && 'pointer-events-none opacity-50',
                    )}
                  >
                    <span>{option.label}</span>
                    {selected && <Check className="size-4 shrink-0" />}
                  </li>
                );
              })
            )}
          </ul>
        </PopoverContent>
      </Popover>
      {tooltip && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={`About the ${label.toLowerCase()} field`}
                className="text-muted-foreground hover:text-foreground"
              >
                <Info className="size-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{tooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

interface HeaderProps {
  /**
   * The counterpart (treaty partner) entity, or null for a general (non-treaty)
   * query. The destination side is fixed to Singapore (the home entity).
   */
  fromJurisdiction: DttJurisdiction | null;
  onFromChange: (jurisdiction: DttJurisdiction | null) => void;
}

export function Header({ fromJurisdiction, onFromChange }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  function handleLogout() {
    logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="flex w-full items-center justify-between gap-4">
      <h1 className="text-lg font-semibold">Aegiss</h1>
      <div className="flex items-center gap-3">
        {/*
          Money flows FROM the counterpart entity TO Singapore. Every indexed
          Double Tax Treaty is Singapore ↔ a partner, so the destination is
          fixed to Singapore and the user only picks the counterpart — or "No
          treaty" for a general Singapore tax query.
        */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled
            aria-label="Destination entity"
            className={cn(
              buttonVariants({ variant: 'outline', size: 'sm' }),
              'min-w-[10rem] justify-start disabled:opacity-100',
            )}
          >
            {HOME_JURISDICTION}
          </button>
          <ArrowRight
            className="text-muted-foreground size-4 shrink-0"
            aria-hidden
          />
          <EntityDropdown
            label="Source"
            direction="from"
            ariaLabel="Source entity"
            placeholder="No treaty (general)"
            value={fromJurisdiction}
            disabledOption={HOME_JURISDICTION}
            allowNone
            tooltip="Optional. For cross-border queries, select a counterparty jurisdiction in order to apply the relevant Double Tax Treaty (DTT) with Singapore. Leave as 'No treaty' for domestic Singapore tax analysis."
            onChange={onFromChange}
          />
        </div>
        <SidebarTrigger />
        {user && (
          <button
            type="button"
            onClick={handleLogout}
            className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }))}
            aria-label="Sign out"
          >
            <LogOut className="size-4" />
          </button>
        )}
      </div>
    </div>
  );
}
